import { FC, KeyboardEvent, useCallback, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import TextField from '@material-ui/core/TextField';
import Loading from 'components/loading/Loading';
import useAuth from 'helpers/useAuth';
import useQueryParams from 'helpers/useQueryParams';
import mixpanel from 'mixpanel-browser';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { LoginModel } from 'shared/auth/model/login.model';
import appRoutes from 'utils/routes';

import {
    ClickableSpan,
    Heading,
    InputsContainer,
    StyledAlert,
    StyledButton,
    StyledForm,
    SubHeading,
    SubText,
} from '../../Authentication.styles';

import { loginSchema } from './schema';

const EMAIL_QUERY = 'emailAddress';

const Login: FC = () => {
    const { t } = useTranslation('authentication');
    const history = useHistory();
    const { search } = useLocation();
    const query = useQueryParams();
    const { show } = useIntercom();

    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [incorrectCode, setHasIncorrectCode] = useState(false);
    const [expiredCode, setHasExpiredCode] = useState(false);
    const [session, setSession] = useState('');
    const [confirmView, setConfirmView] = useState(false);

    const { isLoggedIn } = useAuth();

    const emailAddress = query.get(EMAIL_QUERY) || '';

    const {
        register,
        control,
        handleSubmit,
        getValues,
        formState: { errors, isValid },
    } = useForm<LoginModel>({
        resolver: yupResolver(loginSchema(confirmView)),
        reValidateMode: 'onChange',
        mode: 'onChange',
        defaultValues: {
            email: emailAddress,
        },
    });
    const {
        actions: { confirmOTP, setLoginResult, requestOTP },
        loginResult,
    } = useAuth();

    useEffect(() => {
        return () => {
            setLoginResult('');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isLoggedIn) {
            history.push({
                pathname: appRoutes.home.root,
            });
        }
    }, [isLoggedIn, history]);

    useEffect(() => {
        if (loginResult === 'success') {
            setIsLoading(false);
            mixPanelAnalytics();

            const queryParams = new URLSearchParams(search);

            if (queryParams.has(EMAIL_QUERY)) {
                queryParams.delete(EMAIL_QUERY);
            }

            history.push({
                pathname: appRoutes.home.root,
                search: '?' + queryParams.toString(),
            });
        } else if (
            typeof loginResult === 'object' &&
            loginResult.status === 'incorrect_code'
        ) {
            setIsLoading(false);
            setHasIncorrectCode(true);
            setSession(loginResult.session);
        } else if (loginResult === 'expired_code') {
            setIsLoading(false);
            setHasExpiredCode(true);
        } else if (loginResult === '') {
            setHasError(false);
            setHasIncorrectCode(false);
        } else {
            setIsLoading(false);
            setHasError(true);
        }
    }, [history, loginResult, search]);

    const onSubmit = useCallback(
        async (data: LoginModel) => {
            setIsLoading(true);
            setHasError(false);
            setHasIncorrectCode(false);

            if (!confirmView) {
                const response = await requestOTP(data.email);
                if (response !== 'failed') {
                    setSession(response);
                }
                setConfirmView(true);
                setIsLoading(false);
            } else {
                confirmOTP({ ...data, session });
            }
        },
        [confirmOTP, confirmView, requestOTP, session],
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>): void => {
            // Ignore space key
            if (event.key === ' ') {
                event.preventDefault();
            }

            hasError && isValid && setLoginResult('');
        },
        [hasError, isValid, setLoginResult],
    );

    const handleCodeKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>): void => {
            const allowedKeys = [
                'Backspace',
                'ArrowLeft',
                'ArrowRight',
                'Tab',
                'Delete',
            ];
            const isNumber = /^[0-9]$/.test(event.key);

            if (
                (event.ctrlKey || event.metaKey) &&
                event.key.toLowerCase() === 'v'
            ) {
                return;
            }

            if (!isNumber && !allowedKeys.includes(event.key)) {
                event.preventDefault();
            }

            incorrectCode && isValid && setLoginResult('');
        },
        [incorrectCode, isValid, setLoginResult],
    );

    const mixPanelAnalytics = () => {
        mixpanel.identify(localStorage?.user_email);
        mixpanel.track('Login');
    };

    const handleResendCode = async (email: string) => {
        setIsLoading(true);
        setHasIncorrectCode(false);
        setHasExpiredCode(false);

        const response = await requestOTP(email);
        if (response !== 'failed') {
            setSession(response);
        }
        setIsLoading(false);
    };

    return (
        <div>
            {hasError ? (
                <StyledAlert severity='error'>
                    Sorry you’re experiencing difficulties logging in, please
                    contact us at support@selinafinance.co.uk or{' '}
                    <span
                        style={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            color: 'inherit',
                        }}
                        onClick={() => show()}
                    >
                        use the live chat.
                    </span>
                </StyledAlert>
            ) : (
                <>
                    {confirmView && (
                        <>
                            <Heading>{t('register.sign-in')}</Heading>
                            <SubHeading>
                                {t('login.form.sub-heading')}
                            </SubHeading>
                        </>
                    )}

                    <StyledForm onSubmit={handleSubmit(onSubmit)} noValidate>
                        <InputsContainer>
                            {!confirmView && (
                                <Controller
                                    control={control}
                                    name='email'
                                    render={({
                                        field: { onChange, onBlur, value, ref },
                                    }) => (
                                        <TextField
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            value={value}
                                            inputRef={ref}
                                            placeholder={t('login.form.email')}
                                            type='email'
                                            error={!!errors.email}
                                            helperText={
                                                errors?.email?.type ===
                                                    'email' &&
                                                errors?.email?.message
                                            }
                                            fullWidth
                                            onKeyDown={handleKeyDown}
                                            disabled={!!emailAddress}
                                        />
                                    )}
                                />
                            )}
                            {confirmView && (
                                <TextField
                                    {...register('otp')}
                                    id='otp'
                                    placeholder={t('login.form.code')}
                                    type='text'
                                    fullWidth
                                    error={!!errors.otp}
                                    onKeyDown={handleCodeKeyDown}
                                    inputProps={{
                                        inputMode: 'numeric',
                                        maxLength: 6,
                                    }}
                                />
                            )}
                        </InputsContainer>
                        {confirmView && (
                            <SubText>
                                {t('login.form.resend-info')}{' '}
                                <ClickableSpan
                                    onClick={() =>
                                        handleResendCode(getValues('email'))
                                    }
                                >
                                    {t('login.form.resend-click')}
                                </ClickableSpan>
                                .
                            </SubText>
                        )}
                        {(incorrectCode || expiredCode) && (
                            <StyledAlert severity='error'>
                                {incorrectCode
                                    ? t('login.form.incorrect-code')
                                    : t('login.form.expired-code')}
                            </StyledAlert>
                        )}
                        <StyledButton
                            variant='contained'
                            color='secondary'
                            type='submit'
                            fullWidth
                            disabled={isLoading || !isValid}
                        >
                            {isLoading ? (
                                <Loading />
                            ) : confirmView ? (
                                t('register.sign-in')
                            ) : (
                                t('login.form.continue')
                            )}
                        </StyledButton>
                    </StyledForm>
                </>
            )}
        </div>
    );
};

export default Login;
